import { call, SagaIterator, select } from './sagaEffects';
import { authStateSelector, requestsNotInProgress } from './selectors';
import { BaseRequest, PasswordTokenRequest, RefreshTokenRequest } from 'acscp/api/requests';
import { Authentication, getRequestType } from 'acscp/api/requests';
import { waitFor } from './waitFor';
import { refreshLoginTokenIfNecessary } from './refreshLoginTokenIfNecessary';
import { AppState } from 'acscp/store';

export interface AuthenticationDetails {
    token?: string;
    sharingTokens: string[];
}

const authenticationRequests = [PasswordTokenRequest, RefreshTokenRequest];
const authenticationRequestTypes = authenticationRequests.map(req => getRequestType(req));
export const noAuthenticationRequestsRunning = requestsNotInProgress(authenticationRequestTypes);

export const stateIsRehydrated = (state: AppState) => state.storeStatus.storeRehydrationComplete;

/**
 * Get authentication details to use with the specified request.
 * @param request Request to get authentication details for
 * @returns Authentication details if they are sufficient for running the request. Null, otherwise.
 */
export function* getRequiredAuthenticationDetails(request: BaseRequest<any>): SagaIterator {
    // We want to provide as much auth information to the request as possible,
    // since some endpoints might return more data.
    // For this we need to make sure that we got all data previously persisted in local storage.
    // So we wait until we got a fully rehydrated store.
    yield call(waitFor, stateIsRehydrated);

    // If JWT is missing, try to refresh it.
    yield call(refreshLoginTokenIfNecessary);

    // If the request is not an authentication request,
    // then wait for all authentication requests to complete first, so we could use any updated details.
    const requestIsNotAuth = !authenticationRequests.find(authRequest => request instanceof authRequest);
    if (requestIsNotAuth) {
        yield call(waitFor, noAuthenticationRequestsRunning);
    }

    const auth = (yield select(authStateSelector)) as unknown as AuthenticationDetails;

    return authenticationDetailsAreSufficient(auth, request)
        ? auth
        : null;
}

function authenticationDetailsAreSufficient(auth: AuthenticationDetails, request: BaseRequest<any>): boolean {

    const noAuthRequired = request.authenticationRequired === Authentication.None;

    const userIsLoggedIn = !!auth.token;

    const requestCanBeExecutedWithSharingTokens =
        request.authenticationRequired === Authentication.SharingToken &&
        auth.sharingTokens.length > 0;

    return noAuthRequired || userIsLoggedIn || requestCanBeExecutedWithSharingTokens;
}
